import { useState, useRef, useCallback, useEffect } from 'react';
import { getCompatibleTags } from '@/lib/d2dTagRules';
import { openNavigationForAddress } from '@/utils/navigationUtils';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Users, ArrowLeft, MessageSquare, Clock, Calendar, Pencil, Check, X, UserPlus, Trash2, MapPin, Loader2, Eye, EyeOff, Search, Filter, Camera, ChevronUp, ChevronDown, User, Clock3, CalendarClock, Target, StickyNote, Map as MapIcon } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { OCRScanner } from './OCRScanner';
import { BulkUnitCreator } from './BulkUnitCreator';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { D2DTagSelector } from './D2DTagSelector';
import { CustomerVerificationDialog } from './CustomerVerificationDialog';
import type { D2DUnit, D2DCreateUnitData, D2DTag, D2DHouse } from '@/types/d2d';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useCustomerManagement } from '@/hooks/useCustomerManagement';
import { useAddressSearch } from '@/hooks/useAddressSearch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CustomerFormData, AddressSuggestion } from '@/types/customer';

// Constants for state persistence
const UNIT_LIST_SCROLL_KEY = 'unit-list-scroll-position';
const UNIT_LIST_FILTER_KEY = 'unit-list-filter-state';
interface D2DUnitListProps {
  units: D2DUnit[];
  loading: boolean;
  focusedUnitId?: string;
  location?: {
    street: string;
    plz: string;
    houseNumber: string;
  } | null;
  house?: D2DHouse | null;
  onCreateUnit: (unitData: D2DCreateUnitData, options?: { silent?: boolean }) => Promise<void>;
  onCreateUnitsBatch?: (units: Array<{ name: string; notes?: string }>, onProgress?: (created: number, total: number) => void) => Promise<unknown[]>;
  onUpdateTags: (unitId: string, tags: D2DTag[]) => Promise<void>;
  onUpdateTagsOnly: (unitId: string, tags: D2DTag[]) => Promise<void>;
  onAddNote: (unitId: string, note: string) => Promise<void>;
  onDeleteNote: (noteId: string, unitId: string) => Promise<void>;
  onUpdateUnitName: (unitId: string, name: string) => Promise<void>;
  onCreateFollowUp: (unitId: string, tag: D2DTag, reminderDate: Date, notes?: string, isManual?: boolean, customerEmail?: string) => Promise<void>;
  onDeleteUnit?: (unitId: string) => Promise<void>;
  onBack: () => void;
  onNavigateToCustomer?: (customerId: string) => void;
  onRefreshUnits?: (houseId: string) => Promise<void>;
  onAddHouseNote?: (houseId: string, note: string) => Promise<void>;
  onUpdateHouseNote?: (noteId: string, note: string) => Promise<void>;
  onDeleteHouseNote?: (houseId: string, noteId: string) => Promise<void>;
  isAreaInactive?: boolean;
}

// SwipeableUnitCard component
interface SwipeableUnitCardProps {
  unit: D2DUnit;
  isFocused?: boolean;
  unitRef?: (node: HTMLDivElement | null) => void;
  location?: {
    street: string;
    plz: string;
    houseNumber: string;
  } | null;
  showNotesGlobally: boolean;
  unitNotesVisibility: {
    [unitId: string]: boolean;
  };
  onToggleUnitNotes: (unitId: string) => void;
  onUpdateTags: (unitId: string, tags: D2DTag[]) => Promise<void>;
  onUpdateTagsOnly: (unitId: string, tags: D2DTag[]) => Promise<void>;
  onAddNote: (unitId: string, note: string) => Promise<void>;
  onDeleteNote: (noteId: string, unitId: string) => Promise<void>;
  onUpdateUnitName: (unitId: string, name: string) => Promise<void>;
  onCreateFollowUp: (unitId: string, tag: D2DTag, reminderDate: Date, notes?: string, isManual?: boolean, customerEmail?: string) => Promise<void>;
  onOpenActionDialog: (unit: D2DUnit) => void;
  onDelete?: () => void;
  showDelete?: boolean;
  onNavigateToCustomer: (customerId: string) => void;
  onRefreshUnits?: (houseId: string) => Promise<void>;
  isAreaInactive?: boolean;
  onUpdateEmail: (unitId: string, email: string) => Promise<void>;
}
const SwipeableUnitCard = ({
  unit,
  isFocused,
  unitRef,
  location,
  showNotesGlobally,
  unitNotesVisibility,
  onToggleUnitNotes,
  onUpdateTags,
  onUpdateTagsOnly,
  onAddNote,
  onDeleteNote,
  onUpdateUnitName,
  onCreateFollowUp,
  onOpenActionDialog,
  onDelete,
  showDelete,
  onNavigateToCustomer,
  onRefreshUnits,
  isAreaInactive = false,
  onUpdateEmail
}: SwipeableUnitCardProps) => {
  const {
    getLocale
  } = useLanguage();
  const {
    toast
  } = useToast();
  
  // Check if unit is still being created (pending state)
  const isPending = unit.id.startsWith('temp-');
  const handleSmartNAFollowUp = async (unitId: string, result: 'NA', unit: D2DUnit) => {
    try {
      console.log('🚀 Calling smart-followup-scheduling function for unit:', unitId)

      // Check if KT tag was present before NA
      const currentTags = unit.tags as D2DTag[]
      const hadKTTag = currentTags.includes('KT')

      const { data, error } = await supabase.functions.invoke('smart-followup-scheduling', {
        body: {
          unit_id: unitId,
          visit_result: result,
        },
      })

      if (error) {
        console.error('❌ Error calling smart-followup-scheduling:', error)
        toast({
          title: 'Fehler',
          description: 'Fehler beim Planen des Follow-ups',
          variant: 'destructive',
        })
        return
      }

      console.log('✅ Smart follow-up response:', data)

      if (data?.showCompletionDialog) {
        toast({
          title: 'Alle Zeitslots versucht',
          description:
            data.message ||
            'Die Wohneinheit konnte nicht erreicht werden. Möchten Sie als KT abschließen?',
          variant: 'default',
        })
      } else if (data?.nextFollowUp) {
        toast({
          title: 'Follow-up geplant',
          description:
            data.message ||
            `Nächster Follow-up: ${new Date(data.nextFollowUp).toLocaleString('de-DE')}`,
          variant: 'default',
        })
      }

      // If KT was replaced by NA, add note
      if (hadKTTag) {
        try {
          await onAddNote(unitId, 'Termin nicht wahrgenommen')
          console.log('📝 Added "Termin nicht wahrgenommen" note for replaced KT tag')
        } catch (noteError) {
          console.error('❌ Error adding note for replaced KT:', noteError)
        }
      }

      // NOTE: Kein hartes Reload der Units hier – Realtime-Subscription in useD2DData
      // aktualisiert die Daten automatisch, damit die Liste nicht springt.

    } catch (error) {
      console.error('❌ Error in smart NA follow-up:', error)
      toast({
        title: 'Fehler',
        description: 'Fehler beim Planen des intelligenten Follow-ups',
        variant: 'destructive',
      })
    }
  }

  const cardRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const currentXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isSwipingRef = useRef(false);
  const animationFrameRef = useRef<number>();
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  
  // Optimistic tags state for instant UI feedback
  const [optimisticTags, setOptimisticTags] = useState<D2DTag[] | null>(null);
  const displayTags = optimisticTags ?? (unit.tags as D2DTag[]);
  
  // Use displayTags for disabled state check
  const isDisabledUnit = displayTags?.includes('KI') || displayTags?.includes('NO') || displayTags?.includes('WIN');
  
  // Calculate is_hot based on displayTags for optimistic UI updates
  // CRITICAL: KI/NO/WIN always disable HOT status immediately
  const isHotDisplay = (() => {
    if (displayTags?.includes('KI') || displayTags?.includes('NO') || displayTags?.includes('WIN')) return false;
    if (displayTags?.includes('HOT')) return true;
    if (displayTags?.includes('KT') && unit.last_contact_at) {
      const daysSince = (Date.now() - new Date(unit.last_contact_at).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince >= 3 && daysSince <= 7) return true;
    }
    return false;
  })();
  
  // Reset optimistic state when unit changes
  useEffect(() => {
    setOptimisticTags(null);
  }, [unit.tags]);
  const updateTransform = useCallback((x: number) => {
    if (cardRef.current && backgroundRef.current) {
      const clampedX = Math.min(0, Math.max(-80, x));
      cardRef.current.style.transform = `translateX(${clampedX}px)`;

      // Show delete background when swiping left
      const opacity = Math.abs(clampedX) / 80;
      backgroundRef.current.style.opacity = opacity.toString();
    }
  }, []);
  const resetPosition = useCallback(() => {
    if (cardRef.current && backgroundRef.current) {
      cardRef.current.style.transform = 'translateX(0px)';
      cardRef.current.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      backgroundRef.current.style.opacity = '0';
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.style.transition = '';
        }
      }, 300);
    }
  }, []);
  const handleStart = useCallback((clientX: number, clientY: number) => {
    if (!showDelete) return;
    startXRef.current = clientX;
    startYRef.current = clientY;
    currentXRef.current = 0;
    isDraggingRef.current = true;
    isSwipingRef.current = false;
  }, [showDelete]);
  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!isDraggingRef.current || !showDelete) return;
    const deltaX = clientX - startXRef.current;
    const deltaY = clientY - startYRef.current;

    // Only start swiping if movement is primarily horizontal and to the left
    if (!isSwipingRef.current) {
      const minSwipeDistance = 15; // Minimum distance before considering it a swipe
      const maxVerticalDeviation = 30; // Maximum vertical movement allowed

      // Check if movement is significant enough and primarily horizontal
      if (Math.abs(deltaX) < minSwipeDistance) return;
      if (Math.abs(deltaY) > maxVerticalDeviation) return;
      if (deltaX > 0) return; // Only allow left swipes (negative deltaX)

      // Start swipe mode
      isSwipingRef.current = true;
      if (cardRef.current) {
        cardRef.current.style.transition = '';
      }
    }

    // Only update transform if we're in swipe mode
    if (isSwipingRef.current) {
      currentXRef.current = deltaX;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(() => {
        updateTransform(deltaX);
      });
    }
  }, [updateTransform, showDelete]);
  const handleEnd = useCallback(() => {
    if (!isDraggingRef.current || !showDelete) return;
    isDraggingRef.current = false;
    const wasSwipping = isSwipingRef.current;
    isSwipingRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Only trigger delete if we were actually swiping and swiped far enough
    if (wasSwipping && currentXRef.current < -40 && onDelete) {
      onDelete();
    }
    resetPosition();
  }, [resetPosition, onDelete, showDelete]);

  // Mouse events
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleStart(e.clientX, e.clientY);
  }, [handleStart]);
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);
  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);
  const handleMouseLeave = useCallback(() => {
    if (isDraggingRef.current) {
      handleEnd();
    }
  }, [handleEnd]);

  // Touch events
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      handleStart(touch.clientX, touch.clientY);
    }
  }, [handleStart]);
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      handleMove(touch.clientX, touch.clientY);
    }
  }, [handleMove]);
  const handleTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);
  const handleUpdateName = async () => {
    if (!editingUnitId || !editingName.trim()) return;
    setUpdatingName(true);
    try {
      await onUpdateUnitName(editingUnitId, editingName.trim());
      setEditingUnitId(null);
      setEditingName('');
    } finally {
      setUpdatingName(false);
    }
  };
  const startEditing = (unit: D2DUnit) => {
    setEditingUnitId(unit.id);
    setEditingName(unit.unit_name || '');
  };
  const cancelEditing = () => {
    setEditingUnitId(null);
    setEditingName('');
  };
  const handleTagUpdate = async (unit: D2DUnit, newTags: D2DTag[]) => {
    // Skip database updates for temporary units and show user feedback
    if (unit.id.startsWith('temp-')) {
      console.log('⏳ Skipping tag update for temporary unit:', unit.id);
      toast({
        title: "Bitte warten",
        description: "Die Einheit wird noch gespeichert. Bitte versuchen Sie es in einem Moment erneut.",
        variant: "default"
      });
      return;
    }

    const oldTags = unit.tags as D2DTag[];
    
    // Skip if tags haven't actually changed (prevents double updates)
    const tagsChanged = JSON.stringify([...oldTags].sort()) !== JSON.stringify([...newTags].sort());
    if (!tagsChanged) {
      console.log('ℹ️ Tags unchanged, skipping update');
      return;
    }
    
    // OPTIMISTIC UPDATE: Immediately update UI before database call
    setOptimisticTags(newTags);

    const hadWIN = oldTags.includes('WIN');
    const hasWIN = newTags.includes('WIN');
    const hadKT = oldTags.includes('KT');
    const hasNA = newTags.includes('NA');
    const hasKI = newTags.includes('KI');
    const hasNO = newTags.includes('NO');
    const kiAdded = !oldTags.includes('KI') && hasKI;
    const noAdded = !oldTags.includes('NO') && hasNO;
    
    console.log('🎯 handleTagUpdate called:', {
      unitId: unit.id,
      oldTags,
      newTags,
      changes: {
        added: newTags.filter(t => !oldTags.includes(t)),
        removed: oldTags.filter(t => !newTags.includes(t))
      }
    });

    try {
      // Check if KT was changed to NA - send missed visit email
      if (hadKT && hasNA && !oldTags.includes('NA')) {
        console.log('📧 KT changed to NA - sending missed visit email');
        try {
          const {
            data,
            error
          } = await supabase.functions.invoke('send-missed-visit-email', {
            body: {
              unit_id: unit.id
            }
          });
          if (error) {
            console.error('Error sending missed visit email:', error);
            toast({
              title: "Fehler",
              description: "E-Mail konnte nicht gesendet werden",
              variant: "destructive"
            });
          } else {
            console.log('✅ Missed visit email sent:', data);
            toast({
              title: "E-Mail gesendet",
              description: "Kunde wurde über den verpassten Termin informiert",
              variant: "default"
            });
          }
        } catch (error) {
          console.error('Error in missed visit email flow:', error);
        }
      }

      // Check if NA was added - smart scheduling will handle follow-up
      // D2DTagSelector already saved tags via onUpdateTagsOnly, so skip DB update here
      const naAdded = !oldTags.includes('NA') && newTags.includes('NA');
      const bkAdded = !oldTags.includes('BK') && newTags.includes('BK');
      const bkRemoved = oldTags.includes('BK') && !newTags.includes('BK');
      const hotAdded = !oldTags.includes('HOT') && newTags.includes('HOT');
      const hotRemoved = oldTags.includes('HOT') && !newTags.includes('HOT');
      
      console.log('✅ Processing tag update', { naAdded, kiAdded, noAdded, hasWIN, bkAdded, hotAdded });

      // If WIN tag was added, clear follow-up
      if (!hadWIN && hasWIN) {
        // Update tags first, then clear follow-up by updating with null reminder
        await onUpdateTags(unit.id, newTags);

        // Clear the follow-up by calling the backend to set next_reminder to null
        try {
          const {
            error
          } = await supabase.from('d2d_units').update({
            next_reminder: null,
            reminder_reason: null,
            follow_up_notes: null
          }).eq('id', unit.id);
          if (error) {
            console.error('Error clearing follow-up for WIN tag:', error);
          } else {
            console.log('Follow-up cleared for WIN tag');
          }
        } catch (error) {
          console.error('Error clearing follow-up:', error);
        }
        return;
      }

      // If KI or NO tag was added, the D2DTagSelector already saved tags via onUpdateTagsOnly
      // We just need to clear last contact information here
      if (kiAdded || noAdded) {
        console.log('🔴 KI or NO tag added - clearing last contact information');
        
        // Clear last contact information (tags are already saved by D2DTagSelector)
        try {
          const {
            error
          } = await supabase.from('d2d_units').update({
            last_contact_at: null,
            contact_attempts_count: 0,
            next_reminder: null,
            reminder_reason: null,
            follow_up_notes: null
          }).eq('id', unit.id);
          if (error) {
            console.error('Error clearing last contact for KI/NO tag:', error);
          } else {
            console.log('Last contact and follow-ups cleared for KI/NO tag');
          }
        } catch (error) {
          console.error('Error clearing last contact:', error);
        }
        return;
      }
      
      // If NA, BK or HOT was added/removed via D2DTagSelector with onUpdateTagsOnly,
      // tags are already saved - skip redundant DB update
      if (naAdded || bkAdded || bkRemoved || hotAdded || hotRemoved) {
        console.log('ℹ️ NA/BK/HOT tag change - tags already saved via onUpdateTagsOnly, skipping redundant update');
        return;
      }

      // Normal tag update for all other cases
      await onUpdateTags(unit.id, newTags);
    } catch (error) {
      // REVERT OPTIMISTIC UPDATE on failure
      console.error('❌ Tag update failed, reverting optimistic state:', error);
      setOptimisticTags(null);
      toast({
        title: "Fehler",
        description: "Tag konnte nicht aktualisiert werden",
        variant: "destructive"
      });
    }
  };
  return <div className="relative overflow-hidden rounded-lg">
      {/* Delete Background */}
      {showDelete && <div ref={backgroundRef} className="absolute inset-0 bg-red-500 flex items-center justify-end pr-6 opacity-0">
          <Trash2 className="h-6 w-6 text-white" />
        </div>}
      
      {/* Main Card */}
      <Card ref={node => {
      cardRef.current = node;
      if (unitRef && node) {
        unitRef(node);
      }
    }} className={cn(
      "p-4 transition-all relative z-10", 
      isHotDisplay && "ring-2 ring-destructive/20 bg-destructive/5 animate-border-travel animate-hot-pulse", 
      isFocused && "ring-2 ring-primary shadow-lg bg-primary/5",
      isPending && "opacity-50 pointer-events-none"
    )} onMouseDown={(showDelete && !editingUnitId && !isPending) ? handleMouseDown : undefined} onMouseMove={(showDelete && !editingUnitId && !isPending) ? handleMouseMove : undefined} onMouseUp={(showDelete && !editingUnitId && !isPending) ? handleMouseUp : undefined} onMouseLeave={(showDelete && !editingUnitId && !isPending) ? handleMouseLeave : undefined} onTouchStart={(showDelete && !editingUnitId && !isPending) ? handleTouchStart : undefined} onTouchMove={(showDelete && !editingUnitId && !isPending) ? handleTouchMove : undefined} onTouchEnd={(showDelete && !editingUnitId && !isPending) ? handleTouchEnd : undefined} style={{
      userSelect: 'none',
      touchAction: (showDelete && !editingUnitId && !isPending) ? 'pan-y' : 'auto'
    }}>
        {/* Loading overlay for pending units */}
        {isPending && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-[1px] rounded-lg z-20">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Wird erstellt...</span>
            </div>
          </div>
        )}
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {isHotDisplay && <Badge variant="destructive" className="text-xs mb-1">
                  🔥 HOT
                </Badge>}
              <div className="flex items-center gap-2">
                {editingUnitId === unit.id ? <div className="flex items-center gap-2">
                    <Input value={editingName} onChange={e => setEditingName(e.target.value)} className="h-6 text-sm" placeholder="Name eingeben..." onKeyDown={e => {
                  if (e.key === 'Enter') handleUpdateName();
                  if (e.key === 'Escape') cancelEditing();
                }} autoFocus />
                    <Button variant="ghost" size="sm" onClick={handleUpdateName} disabled={updatingName} className="h-6 w-6 p-0">
                      <Check className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={cancelEditing} className="h-6 w-6 p-0">
                      <X className="h-3 w-3" />
                    </Button>
                  </div> : <>
                    {/* Show customer link if customer exists */}
                     {unit.customer_id && unit.customer_first_name && unit.customer_last_name ? <div className="flex items-center gap-2">
                        <button 
                          onClick={() => onNavigateToCustomer(unit.customer_id!)} 
                          className={cn(
                            "font-medium text-primary hover:text-primary/80 transition-colors",
                            (unit.tags.includes('KI') || unit.tags.includes('NO')) && "opacity-40 text-muted-foreground"
                          )}
                        >
                          {unit.customer_first_name} {unit.customer_last_name}
                        </button>
                        <Badge variant="secondary" className="text-xs">
                          Kunde
                        </Badge>
                      </div> : unit.unit_name ? <div className="flex items-center gap-2">
                        <h4 className={cn(
                          "font-medium",
                          (unit.tags.includes('KI') || unit.tags.includes('NO')) && "opacity-40 text-muted-foreground"
                        )}>{unit.unit_name}</h4>
                        {!isDisabledUnit && !isPending && <Button variant="ghost" size="sm" onClick={() => startEditing(unit)} className="h-6 w-6 p-0 opacity-60 hover:opacity-100">
                            <Pencil className="h-3 w-3" />
                          </Button>}
                      </div> : !isDisabledUnit && !isPending && <Button variant="ghost" size="sm" onClick={() => startEditing(unit)} className="text-muted-foreground hover:text-foreground text-xs h-6 px-2 shrink-0">
                          <Pencil className="h-3 w-3 mr-1" />
                          Name
                        </Button>}
                  </>}
                {unit.offline_sync_pending && <Badge variant="outline" className="text-xs">
                    Sync pending
                  </Badge>}
              </div>
              
              {unit.last_contact_at && !(unit.tags as D2DTag[])?.includes('WIN') && <p className="text-xs text-muted-foreground">
                  {(unit.tags as D2DTag[])?.includes('NA') ? 'Letzter Versuch:' : 'Letzter Kontakt:'} {format(new Date(unit.last_contact_at), 'dd.MM.yyyy HH:mm', {
                locale: getLocale()
              })} ({unit.contact_attempts_count || 0}. Versuch)
                </p>}
            </div>
            
            <div className="flex items-center gap-2">
              {(unit.notes_count || 0) > 0 && <Badge variant="outline" className="text-xs">
                  {unit.notes_count} {unit.notes_count === 1 ? 'Notiz' : 'Notizen'}
                </Badge>}
              {/* Individual notes toggle when global is off */}
              {!showNotesGlobally && (unit.notes_count || 0) > 0 && <Button variant="ghost" size="sm" onClick={() => onToggleUnitNotes(unit.id)} className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground">
                  {unitNotesVisibility[unit.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </Button>}
              {!unit.tags.includes('WIN') && !unit.tags.includes('KI') && !unit.tags.includes('NO') && !isPending && <Button variant="ghost" size="sm" onClick={() => onOpenActionDialog(unit)} className="h-8 w-8 p-0">
                  <Plus className="h-4 w-4" />
                </Button>}
            </div>
          </div>

          {/* Tags */}
          <div className="relative z-20" style={{
          pointerEvents: 'auto'
        }}>
            <D2DTagSelector 
              selectedTags={displayTags} 
              unitId={unit.id}
              unitName={unit.unit_name || unit.customer_first_name || unit.customer_last_name ? `${unit.customer_first_name || ''} ${unit.customer_last_name || ''}`.trim() || unit.unit_name : undefined}
              onTagsChange={isDisabledUnit ? () => {} : tags => handleTagUpdate(unit, tags)} 
              onUpdateTagsOnly={onUpdateTagsOnly} 
              disabled={isDisabledUnit || isAreaInactive || isPending}
              unitNotes={unit.notes || []}
              currentEmail={unit.customer_email || ""}
              onAddNote={onAddNote}
              onUpdateEmail={onUpdateEmail}
              currentAppointment={unit.next_reminder && displayTags.includes('KT') && unit.manual_reminder ? {
              date: new Date(unit.next_reminder),
              notes: unit.follow_up_notes
            } : undefined}
              onAppointmentChanged={() => {
              // Refresh units when appointment changes
              if (onRefreshUnits && unit.house_id) {
                onRefreshUnits(unit.house_id);
              }
            }} 
              onKTFollowUp={async (date, notes, isManual, customerEmail) => {
            console.log('🎯 D2DUnitList: onKTFollowUp callback triggered', {
              unitId: unit.id,
              date,
              notes,
              isDisabledUnit,
              tagToUse: 'KT',
              isManual,
              customerEmail
            });
            if (!isDisabledUnit) {
              console.log('📞 D2DUnitList: Setting KT tag and creating follow-up');

              // WICHTIG: Zentrale Tag-Regeln verwenden!
              const currentTags = unit.tags as D2DTag[];
              const newTags = getCompatibleTags(currentTags, 'KT');
              
              console.log('✅ Using centralized tag rules:', {
                currentTags,
                newTags
              });

              // Update tags first WITHOUT creating automatic follow-up
              await onUpdateTagsOnly(unit.id, newTags);

              // Then create the follow-up with the complex logic
              await onCreateFollowUp(unit.id, 'KT', date, notes, isManual, customerEmail);
            } else {
              console.log('⚠️ D2DUnitList: Skipped - unit is disabled');
            }
          }} onNAFollowUp={async (unitId, result) => {
            console.log('🎯 D2DUnitList: onNAFollowUp callback triggered', {
              unitId,
              result
            });
            // NA follow-ups should only work for units without KI, NO, WIN tags
            const currentTags = unit.tags as D2DTag[];
            const hasDisabledTag = currentTags.includes('KI') || currentTags.includes('NO') || currentTags.includes('WIN');
            if (!hasDisabledTag) {
              await handleSmartNAFollowUp(unitId, result, unit);
            } else {
              console.log('⚠️ D2DUnitList: Skipped smart follow-up - unit has disabled tag (KI/NO/WIN)');
            }
          }} />
          </div>

          {/* Notes */}
          {unit.notes && unit.notes.length > 0 && (showNotesGlobally || unitNotesVisibility[unit.id]) && <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h5 className="text-sm font-medium text-muted-foreground">Notizen:</h5>
                <Button variant="ghost" size="sm" onClick={() => onToggleUnitNotes(unit.id)} className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground">
                  {unitNotesVisibility[unit.id] !== false ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </Button>
              </div>
              {unitNotesVisibility[unit.id] !== false && <div className="space-y-1">
                  {unit.notes.map(note => <div key={note.id} className="bg-muted/30 rounded-md p-2 text-sm relative group">
                      <Button variant="ghost" size="sm" onClick={() => onDeleteNote(note.id, unit.id)} className="absolute top-1 right-1 h-5 w-5 p-0 opacity-100">
                        <X className="h-3 w-3" />
                      </Button>
                      <p className="mb-1 pr-6">{note.note}</p>
                       <p className="text-xs text-muted-foreground">
                         {format(new Date(note.created_at), "dd.MM.yyyy HH:mm", {
                  locale: getLocale()
                })}
                       </p>
                    </div>)}
                </div>}
            </div>}

          {/* Reminder */}
          {unit.next_reminder && !unit.tags.includes('WIN') && !unit.tags.includes('KI') && !unit.tags.includes('NO') && <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>
                Follow-Up: {format(new Date(unit.next_reminder), "dd.MM.yyyy HH:mm", {
              locale: getLocale()
            })}
              </span>
            </div>}
        </div>
      </Card>
    </div>;
};
export const D2DUnitList = ({
  units,
  loading,
  focusedUnitId,
  location,
  house,
  onCreateUnit,
  onCreateUnitsBatch,
  onUpdateTags,
  onUpdateTagsOnly,
  onAddNote,
  onDeleteNote,
  onUpdateUnitName,
  onCreateFollowUp,
  onDeleteUnit,
  onBack,
  onNavigateToCustomer,
  onRefreshUnits,
  onAddHouseNote,
  onUpdateHouseNote,
  onDeleteHouseNote,
  isAreaInactive = false
}: D2DUnitListProps) => {
  const {
    getLocale
  } = useLanguage();
  const {
    toast
  } = useToast();
  const {
    user
  } = useAuth();
  const {
    createCustomer,
    isSubmitting: isCustomerSubmitting
  } = useCustomerManagement();
  const {
    isLoadingAddress,
    addressSuggestions,
    selectedStreet,
    searchStep,
    searchStreets,
    selectAddressSuggestion,
    resetAddressSearch,
    setAddressSuggestions
  } = useAddressSearch();

  // All useState hooks must be at the top level
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBulkCreator, setShowBulkCreator] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [showOCRScanner, setShowOCRScanner] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<D2DUnit | null>(null);
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [unitName, setUnitName] = useState('');
  const [selectedTags, setSelectedTags] = useState<D2DTag[]>([]);
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<D2DUnit | null>(null);

  // Customer creation states
  const CUSTOMER_FORM_STORAGE_KEY = 'd2d-customer-form-draft';
  const [customerForm, setCustomerForm] = useState<CustomerFormData>(() => {
    // Load saved form data from localStorage on mount
    const saved = localStorage.getItem(CUSTOMER_FORM_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          notes: '',
          customerNumber: '',
          servicePin: '',
          iban: '',
          birthDate: ''
        };
      }
    }
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      notes: '',
      customerNumber: '',
      servicePin: '',
      iban: '',
      birthDate: ''
    };
  });

  // Helper function to update customer form and save to localStorage
  const updateCustomerForm = (updates: Partial<CustomerFormData>) => {
    setCustomerForm(prev => {
      const newData = {
        ...prev,
        ...updates
      };
      localStorage.setItem(CUSTOMER_FORM_STORAGE_KEY, JSON.stringify(newData));
      return newData;
    });
  };
  const [isCustomerCreating, setIsCustomerCreating] = useState(false);

  // Notes visibility state
  const [showNotesGlobally, setShowNotesGlobally] = useState(() => {
    const saved = localStorage.getItem('d2d-show-notes-globally');
    return saved === null ? true : saved === 'true';
  });
  const [unitNotesVisibility, setUnitNotesVisibility] = useState<{
    [unitId: string]: boolean;
  }>({});

  // House notes dialog state
  const [showHouseNotesDialog, setShowHouseNotesDialog] = useState(false);
  const [houseNoteText, setHouseNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);

  // Search and filter states with persistence
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagFilters, setSelectedTagFilters] = useState<D2DTag[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortField, setSortField] = useState<'name' | 'last_contact' | 'created_at' | 'none'>('none');
  const [showFilters, setShowFilters] = useState(false);
  const [showFollowupsOnly, setShowFollowupsOnly] = useState(false);

  // Refs
  const unitRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Restore filter state from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(UNIT_LIST_FILTER_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.searchQuery) setSearchQuery(parsed.searchQuery);
        if (parsed.selectedTagFilters) setSelectedTagFilters(parsed.selectedTagFilters);
        if (parsed.sortOrder) setSortOrder(parsed.sortOrder);
        if (parsed.sortField) setSortField(parsed.sortField);
        if (parsed.showFollowupsOnly !== undefined) setShowFollowupsOnly(parsed.showFollowupsOnly);
        console.log('🔄 Unit List: Restored filter state:', parsed);
      }

      // Check for initial tag filter from house navigation
      const initialTagFilter = sessionStorage.getItem('initial-tag-filter');
      if (initialTagFilter) {
        console.log('🏷️ Setting initial tag filter:', initialTagFilter);
        setSelectedTagFilters([initialTagFilter as D2DTag]);
        sessionStorage.removeItem('initial-tag-filter'); // Clear it after use
      }
    } catch (error) {
      console.error('Error restoring filter state:', error);
    }
  }, []);

  // Save filter state whenever it changes
  useEffect(() => {
    const filterState = {
      searchQuery,
      selectedTagFilters,
      sortOrder,
      sortField,
      showFollowupsOnly
    };
    sessionStorage.setItem(UNIT_LIST_FILTER_KEY, JSON.stringify(filterState));
  }, [searchQuery, selectedTagFilters, sortOrder, sortField, showFollowupsOnly]);

  // ==================================================================================
  // CRITICAL SCROLL POSITION PRESERVATION - DO NOT MODIFY
  // ==================================================================================
  // This effect saves and restores scroll position across all interactions
  // 
  // WHY THIS IS CRITICAL:
  // - Users can tag units, add notes, delete notes without losing their position
  // - Scroll position is preserved even during real-time updates from Supabase
  // - Position is saved continuously (on every scroll) to handle any state changes
  //
  // HOW IT WORKS:
  // 1. Saves scroll position to sessionStorage on every scroll event
  // 2. Saves on page unload and visibility change
  // 3. Position is restored when component mounts or data loads
  //
  // ⚠️ DO NOT REMOVE THE SCROLL EVENT LISTENER - it's what makes this work!
  // ==================================================================================
  useEffect(() => {
    const saveScrollPosition = () => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        sessionStorage.setItem(UNIT_LIST_SCROLL_KEY, scrollTop.toString());
      }
    };
    const handleBeforeUnload = () => saveScrollPosition();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saveScrollPosition();
    };

    // ⚠️ CRITICAL: Save scroll position on EVERY scroll event
    // This ensures position is preserved during tag updates, note additions, etc.
    const handleScroll = () => saveScrollPosition();
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener('scroll', handleScroll);
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleScroll);
      }
      saveScrollPosition(); // Save on unmount
    };
  }, []); // ⚠️ Empty dependencies - this must run once and stay active!

  // Toggle global notes visibility
  const toggleGlobalNotesVisibility = () => {
    const newValue = !showNotesGlobally;
    setShowNotesGlobally(newValue);
    localStorage.setItem('d2d-show-notes-globally', newValue.toString());
  };

  // Toggle individual unit notes visibility
  const toggleUnitNotesVisibility = (unitId: string) => {
    setUnitNotesVisibility(prev => ({
      ...prev,
      [unitId]: !prev[unitId]
    }));
  };

  // ==================================================================================
  // CRITICAL SCROLL BEHAVIOR - DO NOT MODIFY WITHOUT TESTING
  // ==================================================================================
  // Auto-scroll to focused unit when focusedUnitId changes
  // This effect MUST ONLY depend on [focusedUnitId] - NOT on [units]
  // 
  // WHY: If we include 'units' in the dependency array, the scroll will trigger
  // every time units update (e.g., when adding notes, changing tags, etc.)
  // This causes the page to jump to the focused unit even when the user is 
  // interacting with a different unit.
  //
  // CORRECT BEHAVIOR:
  // - Only scroll when focusedUnitId is explicitly set (e.g., new unit creation)
  // - DO NOT scroll when units array updates due to edits/notes/tags
  // - Preserve scroll position during all unit modifications
  // ==================================================================================
  useEffect(() => {
    if (focusedUnitId && units.length > 0) {
      const unitElement = unitRefs.current.get(focusedUnitId);
      if (unitElement) {
        // Only scroll if not already in viewport
        const rect = unitElement.getBoundingClientRect();
        const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
        if (!isInViewport) {
          setTimeout(() => {
            unitElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }, 500); // Give time for the component to render
        }
      }
    }
  }, [focusedUnitId]); // ⚠️ CRITICAL: DO NOT add 'units' to dependencies!

  // Monitor scroll position to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      // Show button only when scrolled down past approximately 3 units (assuming ~150px per unit)
      setShowScrollToTop(scrollTop > 450 && units.length > 3);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [units.length]);

  // Scroll to top function
  const scrollToTop = () => {
    // Find the location header element
    const locationHeader = document.querySelector('[data-location-header]');
    if (locationHeader) {
      locationHeader.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      // Fallback to scroll to top if location header not found
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Update unit email
  const handleUpdateEmail = async (unitId: string, email: string) => {
    try {
      const { error } = await supabase
        .from('d2d_units')
        .update({ customer_email: email })
        .eq('id', unitId);

      if (error) throw error;

      toast({
        title: 'E-Mail gespeichert',
        description: 'Die E-Mail-Adresse wurde erfolgreich gespeichert.',
      });
    } catch (error) {
      console.error('Error updating email:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler beim Speichern der E-Mail-Adresse.',
        variant: 'destructive',
      });
    }
  };

  // Restore scroll position after content loads
  useEffect(() => {
    const filteredUnits = [...units].filter(unit => {
      // Search filter
      const matchesSearch = searchQuery === '' || unit.unit_name?.toLowerCase().includes(searchQuery.toLowerCase()) || unit.customer_first_name?.toLowerCase().includes(searchQuery.toLowerCase()) || unit.customer_last_name?.toLowerCase().includes(searchQuery.toLowerCase()) || unit.notes?.some(note => note.note.toLowerCase().includes(searchQuery.toLowerCase()));

      // Tag filter logic - if no tags selected, show all units
      const matchesTagFilter = selectedTagFilters.length === 0 || selectedTagFilters.some(selectedTag => (unit.tags as D2DTag[])?.includes(selectedTag));

      // Follow-up filter - if enabled, only show units with upcoming follow-ups
      const matchesFollowupFilter = !showFollowupsOnly || unit.next_reminder !== null && new Date(unit.next_reminder) > new Date();
      return matchesSearch && matchesTagFilter && matchesFollowupFilter;
    });
    if (filteredUnits.length > 0 && !loading) {
      // Restore scroll position ONLY on initial load or filter changes, not on tag updates
      const timeoutId = setTimeout(() => {
        if (scrollContainerRef.current) {
          const savedScrollPosition = sessionStorage.getItem(UNIT_LIST_SCROLL_KEY);
          const currentScrollTop = scrollContainerRef.current.scrollTop;
          if (savedScrollPosition) {
            const scrollTop = parseInt(savedScrollPosition, 10);

            // Only restore if we're at the very top (0), indicating a page load/filter change
            // Don't restore if user is in the middle of scrolling (prevents jump during tag updates)
            if (currentScrollTop === 0 && scrollTop > 0) {
              scrollContainerRef.current.scrollTop = scrollTop;
              console.log('🔄 Unit List: Restored scroll position:', scrollTop);
            }
          }
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, selectedTagFilters, showFollowupsOnly, loading]); // CRITICAL: Removed 'units' to prevent scroll on tag updates

  // Helper function to avoid Sundays - move to Monday if date falls on Sunday
  const adjustDateForWeekday = (date: Date): Date => {
    const adjustedDate = new Date(date);
    // Check if it's Sunday (getDay() returns 0 for Sunday)
    if (adjustedDate.getDay() === 0) {
      // Move to Monday
      adjustedDate.setDate(adjustedDate.getDate() + 1);
    }
    return adjustedDate;
  };
  const navigate = useNavigate();
  const handleCreateCustomer = async () => {
    if (!selectedUnit || !location || isCustomerCreating || !user) return;
    setIsCustomerCreating(true);
    try {
      // Generate verification token
      const token = crypto.randomUUID();
      setVerificationToken(token);
      setVerificationEmail(customerForm.email);

      // Get organization data
      const {
        data: profile
      } = await supabase.from('profiles').select('organization_id').eq('user_id', user.id).single();
      if (!profile?.organization_id) {
        toast({
          title: "Fehler",
          description: "Keine Organisation gefunden",
          variant: "destructive"
        });
        setIsCustomerCreating(false);
        return;
      }

      // Validate email before sending
      if (!customerForm.email || customerForm.email.trim() === '') {
        console.error('❌ No email provided for opt-in');
        toast({
          title: "Fehler",
          description: "E-Mail-Adresse ist erforderlich für die Datenschutzbestätigung.",
          variant: "destructive"
        });
        setIsCustomerCreating(false);
        return;
      }
      console.log('📧 Sending opt-in email to:', customerForm.email);
      console.log('📧 Organization ID:', profile.organization_id);
      console.log('📧 Token:', token);

      // Store ALL customer data and verification token in d2d_unit (customer will be created AFTER email confirmation)
      const unitNotes = selectedUnit.notes?.map(n => n.note).join('\n\n') || '';
      const {
        error: unitUpdateError
      } = await supabase.from('d2d_units').update({
        customer_email: customerForm.email.trim(),
        customer_first_name: customerForm.firstName,
        customer_last_name: customerForm.lastName,
        customer_phone: customerForm.phone || null,
        customer_address: customerForm.address || null,
        customer_birth_date: customerForm.birthDate || null,
        customer_notes: customerForm.notes || null,
        customer_iban: customerForm.iban || null,
        customer_number: customerForm.customerNumber || null,
        customer_service_pin: customerForm.servicePin || null,
        verification_token: token,
        updated_at: new Date().toISOString()
      }).eq('id', selectedUnit.id);

      if (unitUpdateError) {
        console.error('❌ Error updating unit with customer data:', unitUpdateError);
        toast({
          title: "Fehler",
          description: "Kundendaten konnten nicht gespeichert werden.",
          variant: "destructive"
        });
        setIsCustomerCreating(false);
        return;
      }
      console.log('✅ Customer data stored in unit, awaiting email confirmation');

      // Send opt-in email
      const {
        data: emailResponse,
        error: emailError
      } = await supabase.functions.invoke('send-customer-optin', {
        body: {
          email: customerForm.email.trim(),
          firstName: customerForm.firstName,
          lastName: customerForm.lastName,
          organizationId: profile.organization_id,
          verificationToken: token,
          appUrl: window.location.origin
        }
      });
      console.log('📧 Email response:', emailResponse);
      console.log('📧 Email error:', emailError);
      if (emailError) {
        console.error('❌ Error sending opt-in email:', emailError);
        toast({
          title: "Fehler",
          description: `Die Bestätigungs-E-Mail konnte nicht versendet werden: ${emailError.message}`,
          variant: "destructive"
        });
        setIsCustomerCreating(false);
        return;
      }
      console.log('✅ Opt-in email sent successfully');

      // Show verification dialog
      setShowCustomerDialog(false);
      setShowVerificationDialog(true);
      setIsCustomerCreating(false);
    } catch (error) {
      console.error('Error in handleCreateCustomer:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive"
      });
      setIsCustomerCreating(false);
    }
  };
  const handleVerificationComplete = async () => {
    if (!selectedUnit) return;
    try {
      // Get the unit with the updated customer_id (set by the RPC function)
      const {
        data: updatedUnit
      } = await supabase.from('d2d_units').select('customer_id').eq('id', selectedUnit.id).single();
      if (!updatedUnit?.customer_id) {
        toast({
          title: "Fehler",
          description: "Kunde konnte nach Bestätigung nicht angelegt werden.",
          variant: "destructive"
        });
        return;
      }

      // Customer is already linked to unit by the RPC function
      console.log('✅ Customer created and linked:', updatedUnit.customer_id);
      
      // Update tags (add WIN tag)
      const currentTags = selectedUnit.tags as D2DTag[] || [];
      const newTags = getCompatibleTags(currentTags, 'WIN');
      const {
        error: updateError
      } = await supabase.from('d2d_units').update({
        tags: newTags
      }).eq('id', selectedUnit.id);
      if (updateError) {
        console.error('Error updating unit tags:', updateError);
        toast({
          title: "Warnung",
          description: "Kunde wurde erstellt, aber Tag-Update fehlgeschlagen.",
          variant: "destructive"
        });
      }

      // Reset and close
      resetCustomerForm();
      setShowVerificationDialog(false);
      setVerificationToken('');
      setVerificationEmail('');
      setSelectedUnit(null);

      // Open customer details
      if (onNavigateToCustomer) {
        onNavigateToCustomer(updatedUnit.customer_id);
      }
      toast({
        title: "Erfolg",
        description: "Kunde wurde erfolgreich erstellt und bestätigt."
      });
    } catch (error) {
      console.error('Error completing verification:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive"
      });
    }
  };
  const resetCustomerForm = () => {
    localStorage.removeItem(CUSTOMER_FORM_STORAGE_KEY);
    setCustomerForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      notes: '',
      customerNumber: '',
      servicePin: '',
      iban: '',
      birthDate: ''
    });
  };
  const handleCancelCustomerDialog = () => {
    if (!isCustomerCreating) {
      resetCustomerForm();
      setShowCustomerDialog(false);
      setSelectedUnit(null);
    }
  };
  const openActionDialog = (unit: D2DUnit) => {
    setSelectedUnit(unit);
    setShowActionDialog(true);
  };
  const openCustomerDialog = async (unit: D2DUnit) => {
    setSelectedUnit(unit);

    // Reset address search state to prevent interference
    resetAddressSearch((address: string) => {});
    setAddressSuggestions([]);
    
    const unitNotes = unit.notes?.map(n => n.note).join('\n\n') || '';
    
    // Set basic data immediately
    const basicAddress = location 
      ? `${location.street} ${location.houseNumber}, ${location.plz}, Deutschland`
      : '';
    
    updateCustomerForm({
      address: basicAddress,
      notes: unitNotes,
      lastName: unit.unit_name || '',
      email: unit.customer_email || ''
    });
    
    // Open dialog immediately
    setShowCustomerDialog(true);
    
    // Load complete address in background
    if (location) {
      try {
        const searchQuery = `${location.street} ${location.houseNumber}, ${location.plz}, Deutschland`;
        const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&addressdetails=1&limit=1&countrycodes=de`);
        if (response.ok) {
          const results = await response.json();
          if (results.length > 0) {
            const result = results[0];
            const addr = result.address;
            const street = addr?.road || location.street;
            const houseNumber = addr?.house_number || location.houseNumber;
            const city = addr?.city || addr?.town || addr?.village || addr?.hamlet || addr?.municipality || '';
            const postcode = addr?.postcode || location.plz;
            const state = addr?.state || '';

            // Update with complete address
            const completeAddress = `${street} ${houseNumber}, ${postcode}${city ? ' ' + city : ''}${state ? ', ' + state : ''}, Deutschland`;
            updateCustomerForm({
              address: completeAddress
            });
          }
        }
      } catch (error) {
        console.error('Fehler beim Abrufen der Adresse:', error);
        // Keep basic address if API fails
      }
    }
  };
  const handleDeleteUnit = async () => {
    if (!unitToDelete || !onDeleteUnit) return;

    // Save scroll position before deletion
    if (scrollContainerRef.current) {
      const scrollTop = scrollContainerRef.current.scrollTop;
      sessionStorage.setItem(UNIT_LIST_SCROLL_KEY, scrollTop.toString());
      console.log('💾 Unit List: Saved scroll position before deletion:', scrollTop);
    }
    try {
      await onDeleteUnit(unitToDelete.id);

      // Dispatch event to refresh timeline
      console.log('🔄 Dispatching unitDeleted event for timeline refresh');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('unitDeleted'));
      }
    } finally {
      setDeleteDialogOpen(false);
      setUnitToDelete(null);
    }
  };
  const handleCreate = async () => {
    setCreating(true);
    try {
      await onCreateUnit({
        unit_name: unitName.trim() || undefined,
        tags: selectedTags,
        note: note.trim() || undefined
      });

      // Reset form
      setUnitName('');
      setSelectedTags([]);
      setNote('');
      setShowCreateDialog(false);
    } finally {
      setCreating(false);
    }
  };
  const handleBulkCreate = async (units: Array<{
    name: string;
    notes?: string;
  }>) => {
    if (units.length === 0) return;
    setCreating(true);
    try {
      // PERFORMANCE FIX: Create units in parallel for faster bulk creation
      console.log(`⚡ Bulk creating ${units.length} units in parallel...`);
      
      const createPromises = units.map(unit => 
        onCreateUnit({
          unit_name: unit.name.trim(),
          tags: [],
          note: unit.notes || undefined,
          reminder: undefined
        })
      );
      
      await Promise.all(createPromises);
      
      console.log(`✅ Bulk creation complete for ${units.length} units`);
      
      toast({
        title: "Erfolg",
        description: `${units.length} Wohneinheiten wurden erstellt`
      });

      // Close both dialogs after successful creation
      setShowBulkCreator(false);
      setShowCreateDialog(false);
      
      // FIX: Trigger refresh to ensure real IDs are loaded and tags work correctly
      // Wait a moment for realtime subscription to process all units
      if (onRefreshUnits && house?.id) {
        console.log('🔄 Triggering refresh after bulk creation to sync real IDs...');
        setTimeout(async () => {
          try {
            await onRefreshUnits(house.id);
            console.log('✅ Refresh complete - tags should now work');
          } catch (err) {
            console.error('Error refreshing units:', err);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error creating bulk units:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Erstellen der Wohneinheiten",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };
  const handleAddNote = async () => {
    if (!selectedUnit || !note.trim()) return;

    // Check if unit is still being created (temp ID)
    if (selectedUnit.id.startsWith('temp-')) {
      toast({
        title: "Bitte warten",
        description: "Wohneinheit wird noch erstellt. Bitte versuchen Sie es in einem Moment noch einmal.",
        variant: "destructive"
      });
      return;
    }
    setAddingNote(true);
    try {
      await onAddNote(selectedUnit.id, note.trim());
      setNote('');
      setShowNoteDialog(false);
      setShowActionDialog(false); // Schließe auch das Action Dialog nach erfolgreichem Speichern
      setSelectedUnit(null);
    } catch (error) {
      console.error('Error adding note:', error);
      // Error toast is already shown by the addNote function
    } finally {
      setAddingNote(false);
    }
  };

  // REMOVED: Duplicate handleTagUpdate function - using the first one at line 306 instead
  if (loading) {
    return <div className="space-y-4">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-6 bg-muted rounded w-32 animate-pulse"></div>
        </div>
        {[1, 2].map(i => <Card key={i} className="p-4 animate-pulse">
            <div className="h-5 bg-muted rounded w-1/3 mb-3"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </Card>)}
      </div>;
  }

  // Filter and sort units
  const filteredAndSortedUnits = [...units].filter(unit => {
    // Search filter
    const matchesSearch = searchQuery === '' || unit.unit_name?.toLowerCase().includes(searchQuery.toLowerCase()) || unit.customer_first_name?.toLowerCase().includes(searchQuery.toLowerCase()) || unit.customer_last_name?.toLowerCase().includes(searchQuery.toLowerCase()) || unit.notes?.some(note => note.note.toLowerCase().includes(searchQuery.toLowerCase()));

    // Follow-up filter - if enabled, only show units with scheduled follow-ups
    const matchesFollowupFilter = !showFollowupsOnly || unit.next_reminder !== null && unit.next_reminder !== undefined;

    // Tag filter logic - if no tags selected, show all units
    const isTargetFilter = selectedTagFilters.length === 4 && selectedTagFilters.includes('BK') && selectedTagFilters.includes('HOT') && selectedTagFilters.includes('KT') && selectedTagFilters.includes('NA');
    const matchesTagFilter = selectedTagFilters.length === 0 || selectedTagFilters.some(selectedTag => (unit.tags as D2DTag[])?.includes(selectedTag)) || isTargetFilter && (!unit.tags || unit.tags.length === 0);

    // Target filter excludes: NO, KI, WIN tags
    // Any active filter (except WIN) excludes WIN-tagged units
    const isExcluded = (isTargetFilter && ((unit.tags as D2DTag[])?.includes('NO') || (unit.tags as D2DTag[])?.includes('KI') || (unit.tags as D2DTag[])?.includes('WIN'))) || (selectedTagFilters.length > 0 && !selectedTagFilters.includes('WIN') && (unit.tags as D2DTag[])?.includes('WIN'));
    return matchesSearch && matchesFollowupFilter && matchesTagFilter && !isExcluded;
  }).sort((a, b) => {
    // Only apply HOT priority sorting when explicit sorting is active
    if (sortField !== 'none') {
      // Priority sorting: hot leads first (only when sorting is active)
      if (a.is_hot && !b.is_hot) return -1;
      if (!a.is_hot && b.is_hot) return 1;
    }
    let comparison = 0;
    if (sortField === 'last_contact') {
      // Sort by last contact date
      const aDate = a.last_contact_at ? new Date(a.last_contact_at).getTime() : 0;
      const bDate = b.last_contact_at ? new Date(b.last_contact_at).getTime() : 0;
      comparison = aDate - bDate;
    } else if (sortField === 'name') {
      // Sort by name
      const aName = a.unit_name || `${a.customer_first_name || ''} ${a.customer_last_name || ''}`.trim() || 'Unbenannt';
      const bName = b.unit_name || `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim() || 'Unbenannt';
      comparison = aName.localeCompare(bName);
    } else if (sortField === 'created_at') {
      // Sort by creation date
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      comparison = aDate - bDate;
    } else {
      // No sorting (none) - maintain stable order by ID to prevent jumping when tags change
      comparison = a.id.localeCompare(b.id);
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Get unique tags from all units for filter options
  const availableTags = Array.from(new Set(units.flatMap(unit => unit.tags as D2DTag[]))).filter(Boolean).sort();
  const openMapsForAddress = () => {
    if (!location) return;
    openNavigationForAddress(location.street, location.houseNumber, location.plz);
  };
  return <div ref={scrollContainerRef} className="space-y-4">
      {location && <div className="space-y-3 mb-4">
          <div className="flex flex-col items-center gap-2" data-location-header>
            <div 
              className="flex items-center justify-center gap-3 py-4 px-8 cursor-pointer bg-gradient-to-br from-primary/10 via-background to-accent/10 hover:from-primary/15 hover:via-background hover:to-accent/15 rounded-2xl transition-all border-2 border-primary/20 hover:border-primary/40 shadow-lg backdrop-blur-sm hover:shadow-xl group" 
              onClick={openMapsForAddress}
            >
              <div className="flex flex-col items-center gap-1 max-w-full">
                <div className="flex items-center gap-2 max-w-full">
                  <h2 className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent truncate max-w-[250px]">
                    {location.street}
                  </h2>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent flex-shrink-0">
                    {location.houseNumber}
                  </h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  {location.plz}
                </p>
              </div>
              <MapIcon className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
            </div>
            {(!house?.notes || house.notes.length === 0) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHouseNotesDialog(true)}
                className="gap-2 hover:bg-transparent rounded-sm border border-border"
              >
                <StickyNote className="h-4 w-4" />
                Notiz hinzufügen
              </Button>
            )}
          </div>
          
          {/* Show house notes directly below header */}
          {house?.notes && house.notes.length > 0 && (
            <div className="flex justify-center">
              <div className="max-w-2xl w-full text-xs text-muted-foreground bg-muted/30 px-6 py-3 rounded border-l-2 border-primary/50">
                <div className="flex items-center gap-2 mb-2">
                  <StickyNote className="h-4 w-4 text-primary" />
                  <span className="font-medium">Hausnotizen:</span>
                </div>
                {house.notes.map((note, idx) => (
                  <div key={note.id} className={cn(idx > 0 && "mt-1.5 pt-1.5 border-t border-border/30")}>
                    {editingNoteId === note.id ? (
                      <div className="space-y-2">
                        <Textarea
                          value={houseNoteText}
                          onChange={(e) => setHouseNoteText(e.target.value)}
                          placeholder="Notiz bearbeiten..."
                          className="min-h-[60px] text-xs"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={async () => {
                              if (onUpdateHouseNote && houseNoteText.trim() && house) {
                                await onUpdateHouseNote(note.id, houseNoteText);
                                setEditingNoteId(null);
                                setHouseNoteText('');
                              }
                            }}
                          >
                            Speichern
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingNoteId(null);
                              setHouseNoteText('');
                            }}
                          >
                            Abbrechen
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          {note.note}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              setEditingNoteId(note.id);
                              setHouseNoteText(note.note);
                            }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                            onClick={async () => {
                              if (onDeleteHouseNote && house) {
                                const isLastNote = house.notes?.length === 1;
                                await onDeleteHouseNote(house.id, note.id);
                                
                                // Only clear editing state if it was the last note
                                if (isLastNote) {
                                  setHouseNoteText('');
                                  setEditingNoteId(null);
                                }
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>}
      
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="hover:bg-transparent rounded-sm border border-border">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Users className="h-5 w-5 text-primary" />
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold">Wohneinheiten</h2>
          <Badge variant="outline" className="text-[10px] text-primary border-primary/20 bg-primary/5 flex items-center justify-center">
            {units.length === 1 ? '1' : `${filteredAndSortedUnits.length} von ${units.length}`}
          </Badge>
        </div>
      </div>

      {/* Filter and Notes Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant={selectedTagFilters.length === 4 && selectedTagFilters.includes('BK') && selectedTagFilters.includes('HOT') && selectedTagFilters.includes('KT') && selectedTagFilters.includes('NA') ? 'default' : 'ghost'} size="sm" onClick={() => {
          const isTargetFilterActive = selectedTagFilters.length === 4 && selectedTagFilters.includes('BK') && selectedTagFilters.includes('HOT') && selectedTagFilters.includes('KT') && selectedTagFilters.includes('NA');
          if (isTargetFilterActive) {
            setSelectedTagFilters([]);
          } else {
            setSelectedTagFilters(['BK', 'HOT', 'KT', 'NA']);
          }
        }} className={`gap-2 hover:bg-inherit hover:text-inherit ${selectedTagFilters.length === 4 && selectedTagFilters.includes('BK') && selectedTagFilters.includes('HOT') && selectedTagFilters.includes('KT') && selectedTagFilters.includes('NA') ? 'backdrop-blur-md bg-primary/20 border border-primary/30 text-foreground' : 'text-muted-foreground'}`}>
            <Target className="h-4 w-4" />
          </Button>
          <Button variant={selectedTagFilters.length === 1 && selectedTagFilters.includes('HOT') ? 'default' : 'ghost'} size="sm" onClick={() => {
          const isHotFilterActive = selectedTagFilters.length === 1 && selectedTagFilters.includes('HOT');
          if (isHotFilterActive) {
            setSelectedTagFilters([]);
          } else {
            setSelectedTagFilters(['HOT']);
          }
        }} className={`gap-2 hover:bg-inherit hover:text-inherit ${selectedTagFilters.length === 1 && selectedTagFilters.includes('HOT') ? 'backdrop-blur-md bg-destructive/20 border border-destructive/30 text-destructive' : 'text-muted-foreground'}`}>
            🔥
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-2 text-muted-foreground hover:bg-transparent">
            <Filter className="h-4 w-4" />
            Filter
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={toggleGlobalNotesVisibility} className="gap-2 text-muted-foreground hover:bg-transparent">
          {showNotesGlobally ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          Notizen
        </Button>
      </div>

      {/* Search and Filter Controls */}
      {showFilters && <Card className="p-4 mb-4 space-y-4">
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Tag Filter</Label>
              <div className="flex flex-wrap gap-2">
                <Button variant={selectedTagFilters.length === 0 ? 'default' : 'outline'} size="sm" onClick={() => setSelectedTagFilters([])} className={`text-xs ${selectedTagFilters.length === 0 ? 'backdrop-blur-md bg-primary/20 border border-primary/30 text-foreground' : ''}`}>
                  Alle Tags
                </Button>
                <Button variant={selectedTagFilters.length === 4 && selectedTagFilters.includes('BK') && selectedTagFilters.includes('HOT') && selectedTagFilters.includes('KT') && selectedTagFilters.includes('NA') ? 'default' : 'outline'} size="sm" onClick={() => setSelectedTagFilters(['BK', 'HOT', 'KT', 'NA'])} className={`text-xs ${selectedTagFilters.length === 4 && selectedTagFilters.includes('BK') && selectedTagFilters.includes('HOT') && selectedTagFilters.includes('KT') && selectedTagFilters.includes('NA') ? 'backdrop-blur-md bg-primary/20 border border-primary/30 text-foreground' : ''}`}>
                  <Target className="h-4 w-4" />
                </Button>
                {availableTags.map(tag => <Button key={tag} variant={selectedTagFilters.includes(tag) ? 'default' : 'outline'} size="sm" onClick={() => {
              const newFilters = selectedTagFilters.includes(tag) ? selectedTagFilters.filter(t => t !== tag) : [...selectedTagFilters, tag];
              setSelectedTagFilters(newFilters);
            }} className={`text-xs ${selectedTagFilters.includes(tag) ? 'backdrop-blur-md bg-primary/20 border border-primary/30 text-foreground' : ''}`}>
                    {tag}
                  </Button>)}
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Sortierung</Label>
              <div className="space-y-3">
                <div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} className="h-8 w-8 p-0">
                      {sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                    <div className="w-px h-6 bg-border/50 self-center mx-1"></div>
                    <Button variant={sortField === 'name' ? 'default' : 'outline'} size="sm" onClick={() => setSortField(sortField === 'name' ? 'none' : 'name')} className={`h-8 px-3 text-xs ${sortField === 'name' ? 'backdrop-blur-md bg-primary/20 border border-primary/30 text-foreground' : ''}`}>
                      <User className="h-3 w-3 mr-1" />
                      Name
                    </Button>
                    <Button variant={sortField === 'last_contact' ? 'default' : 'outline'} size="sm" onClick={() => setSortField(sortField === 'last_contact' ? 'none' : 'last_contact')} className={`h-8 px-3 text-xs ${sortField === 'last_contact' ? 'backdrop-blur-md bg-primary/20 border border-primary/30 text-foreground' : ''}`}>
                      <Clock3 className="h-3 w-3 mr-1" />
                      Kontakt
                    </Button>
                    <Button variant={sortField === 'created_at' ? 'default' : 'outline'} size="sm" onClick={() => setSortField(sortField === 'created_at' ? 'none' : 'created_at')} className={`h-8 px-3 text-xs ${sortField === 'created_at' ? 'backdrop-blur-md bg-primary/20 border border-primary/30 text-foreground' : ''}`}>
                      <CalendarClock className="h-3 w-3 mr-1" />
                      Erstellung
                    </Button>
                    <Button variant={showFollowupsOnly ? 'default' : 'outline'} size="sm" onClick={() => {
                  const newShowFollowupsOnly = !showFollowupsOnly;
                  setShowFollowupsOnly(newShowFollowupsOnly);
                  // Clear tag filters when follow-up filter is activated
                  if (newShowFollowupsOnly) {
                    setSelectedTagFilters([]);
                  }
                }} className={`h-8 px-3 text-xs ${showFollowupsOnly ? 'backdrop-blur-md bg-primary/20 border border-primary/30 text-foreground' : ''}`}>
                      <CalendarClock className="h-3 w-3 mr-1" />
                      Follow-up
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>}
      
      {/* Search Field */}
      <div className="flex justify-center mb-4">
        <div className="relative">
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="gap-2 text-muted-foreground/60 focus:text-muted-foreground bg-muted/20 backdrop-blur-sm border border-border/30 rounded-full px-4 py-2 pr-10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 placeholder:text-muted-foreground/40" placeholder="Suchen" inputMode="text" onContextMenu={e => {
          // Allow native context menu with "Scan Text" on iOS
          e.stopPropagation();
        }} onTouchStart={e => {
          // Enable iOS text scanning context menu on long press
          const target = e.target as HTMLInputElement;
          setTimeout(() => {
            if (navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
              target.select();
              target.setSelectionRange(0, 0);
            }
          }, 100);
        }} />
          {searchQuery && <Button variant="ghost" size="sm" onClick={() => setSearchQuery('')} className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-muted-foreground/60 hover:text-muted-foreground rounded-full">
              <X className="h-3 w-3" />
            </Button>}
        </div>
      </div>
      
      {/* Add Unit Button */}
      <div className="flex flex-col items-center gap-2 mb-4">
        {isAreaInactive ? (
          <div className="flex flex-col items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              disabled 
              className="gap-2 text-muted-foreground font-medium bg-muted/10 backdrop-blur-sm border-2 border-border/50 rounded-full px-5 py-2 opacity-50 cursor-not-allowed"
            >
              <Plus className="h-4 w-4" />
              Weitere Einheit hinzufügen
            </Button>
            <span className="text-xs text-muted-foreground">Gebiet ist inaktiv</span>
          </div>
        ) : (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 text-foreground font-medium hover:bg-muted/10 backdrop-blur-sm border-2 border-border rounded-full px-5 py-2 transition-all duration-200">
                <Plus className="h-4 w-4" />
                Weitere Einheit hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Weitere Wohneinheit hinzufügen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="unit-name">Name/Beschreibung (optional)</Label>
                  <div className="relative">
                    <Button type="button" variant="ghost" size="sm" className="absolute left-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-muted z-10" onClick={() => setShowBulkCreator(true)} title="Klingelschild scannen">
                      <Camera className="h-4 w-4" />
                    </Button>
                    <Input id="unit-name" value={unitName} onChange={e => setUnitName(e.target.value)} placeholder="z.B. Familie Schmidt, 1. OG rechts" className="pl-12" inputMode="text" onContextMenu={e => {
                    // Allow native context menu on iOS
                    e.stopPropagation();
                  }} />
                  </div>
                </div>
                
                <div>
                  <Label>Tags (optional)</Label>
                  <div className="mt-2">
                    <D2DTagSelector selectedTags={selectedTags} onTagsChange={setSelectedTags} disabled={isAreaInactive} />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="note">Notiz (optional)</Label>
                  <Textarea id="note" value={note} onChange={e => setNote(e.target.value)} placeholder="Notizen zum Kontakt..." rows={3} />
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">
                    Abbrechen
                  </Button>
                  <Button onClick={handleCreate} disabled={creating} className="flex-1">
                    {creating ? 'Erstelle...' : 'Hinzufügen'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      
      
      
      {filteredAndSortedUnits.length === 0 ? <Card className="p-8 text-center">
          <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            {units.length === 0 ? 'Keine Wohneinheiten vorhanden' : 'Keine Wohneinheiten gefunden'}
          </h3>
          {units.length > 0 && <p className="text-muted-foreground">
              Versuche andere Suchbegriffe oder Filter.
            </p>}
        </Card> : <div className="space-y-3">
          {filteredAndSortedUnits.map(unit => <SwipeableUnitCard key={unit.id} unit={unit} isFocused={focusedUnitId === unit.id} unitRef={node => {
        if (node) {
          unitRefs.current.set(unit.id, node);
        } else {
          unitRefs.current.delete(unit.id);
        }
      }} location={location} showNotesGlobally={showNotesGlobally} unitNotesVisibility={unitNotesVisibility} onToggleUnitNotes={toggleUnitNotesVisibility} onUpdateTags={onUpdateTags} onUpdateTagsOnly={onUpdateTagsOnly} onAddNote={onAddNote} onDeleteNote={onDeleteNote} onUpdateUnitName={onUpdateUnitName} onCreateFollowUp={onCreateFollowUp} onOpenActionDialog={openActionDialog} onDelete={onDeleteUnit ? () => {
        setUnitToDelete(unit);
        setDeleteDialogOpen(true);
      } : undefined} showDelete={!!onDeleteUnit} onNavigateToCustomer={onNavigateToCustomer || (() => {})} onRefreshUnits={onRefreshUnits} isAreaInactive={isAreaInactive} onUpdateEmail={handleUpdateEmail} />)}
        </div>}


      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aktionen für Wohneinheit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedUnit && <div className="text-sm text-muted-foreground mb-4 text-center">
                {selectedUnit.unit_name || 'Wohneinheit'}
              </div>}
            
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="unit-note" className="text-sm font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Notiz hinzufügen
                </Label>
                <Textarea id="unit-note" value={note} onChange={e => setNote(e.target.value)} placeholder="Notiz eingeben..." className="min-h-[80px] resize-none" />
                <Button onClick={handleAddNote} disabled={!note.trim() || addingNote} className="w-full" size="sm">
                  {addingNote ? 'Wird hinzugefügt...' : 'Notiz speichern'}
                </Button>
              </div>
              
              <Button className="w-full justify-center gap-2 py-4 px-6 font-medium rounded-xl bg-card/60 backdrop-blur-sm text-foreground/90 border border-border/40 shadow-md hover:bg-card/80 hover:text-foreground transition-all duration-700 relative before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-primary/10 before:to-accent/10 before:blur-lg before:opacity-0 before:animate-gentle-blur before:-z-10 hover:before:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-card/60" onClick={() => {
              setShowActionDialog(false);
              openCustomerDialog(selectedUnit!);
            }} disabled={selectedUnit?.customer_id !== null}>
                <UserPlus className="h-4 w-4 relative z-10" />
                <span className="relative z-10">
                  {selectedUnit?.customer_id ? 'Kunde bereits vorhanden' : 'Kunde anlegen'}
                </span>
              </Button>
            </div>
            
            <Button variant="outline" onClick={() => setShowActionDialog(false)} className="w-full">
              Abbrechen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Creation Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Neuen Kunden erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Vorname *</Label>
                <Input value={customerForm.firstName} onChange={e => updateCustomerForm({
                firstName: e.target.value
              })} placeholder="Max" />
              </div>
              <div>
                <Label className="text-sm">Nachname *</Label>
                <Input value={customerForm.lastName} onChange={e => updateCustomerForm({
                lastName: e.target.value
              })} placeholder="Mustermann" />
              </div>
            </div>
            
            {/* Address field with autocomplete */}
            <div>
              <Label className="text-sm">Adresse</Label>
              <div className="relative">
                <div className="relative">
                  <Input value={customerForm.address} onChange={e => {
                  const value = e.target.value;
                  updateCustomerForm({
                    address: value
                  });
                  // Keine automatische Suche, wenn Adresse bereits vorausgefüllt ist
                  // Nur bei komplett leerer oder manuell veränderter Eingabe suchen
                  if (value.length < 10 && searchStep === 'street' && value.length >= 3) {
                    searchStreets(value);
                  }
                }} placeholder="Adresse" className="pr-10" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    {isLoadingAddress ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <MapPin className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </div>
                
                {/* Address Suggestions */}
                {addressSuggestions.length > 0 && <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {addressSuggestions.map((suggestion, index) => <button key={index} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground border-b border-border last:border-0" onClick={() => {
                  selectAddressSuggestion(suggestion, (address: string) => {
                    updateCustomerForm({
                      address
                    });
                  });
                }}>
                        <div className="font-medium">{suggestion.displayName}</div>
                        {suggestion.fullAddress && suggestion.displayName !== suggestion.fullAddress && <div className="text-muted-foreground text-xs">
                            {suggestion.fullAddress}
                          </div>}
                      </button>)}
                  </div>}
                
                {/* Reset button */}
                {(selectedStreet || customerForm.address) && <Button type="button" variant="ghost" size="sm" className="absolute top-0 right-8 h-full px-2" onClick={() => {
                resetAddressSearch((address: string) => {
                  updateCustomerForm({
                    address
                  });
                });
              }}>
                    <X className="h-3 w-3" />
                  </Button>}
              </div>
            </div>
            
            <div>
              <Label className="text-sm">Geburtsdatum *</Label>
              <Input type="date" value={customerForm.birthDate} onChange={e => updateCustomerForm({
              birthDate: e.target.value
            })} />
            </div>
            
            <div>
              <Label className="text-sm">Telefon</Label>
              <Input value={customerForm.phone} onChange={e => updateCustomerForm({
              phone: e.target.value
            })} placeholder="+49 123 456789" />
            </div>
            
            <div>
              <Label className="text-sm">E-Mail *</Label>
              <Input type="email" value={customerForm.email} onChange={e => updateCustomerForm({
              email: e.target.value
            })} placeholder="max@beispiel.de" required />
            </div>
            
            <div>
              <Label className="text-sm">Kundennummer</Label>
              <Input value={customerForm.customerNumber} onChange={e => updateCustomerForm({
              customerNumber: e.target.value
            })} placeholder="12345678" />
            </div>
            
            <div>
              <Label className="text-sm">Service-PIN</Label>
              <Input value={customerForm.servicePin} onChange={e => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              updateCustomerForm({
                servicePin: value
              });
            }} placeholder="123456" maxLength={6} />
            </div>
            
            <div>
              <Label className="text-sm">IBAN</Label>
              <Input value={customerForm.iban} onChange={e => updateCustomerForm({
              iban: e.target.value.toUpperCase()
            })} placeholder="DE89 3704 0044 0532 0130 00" />
            </div>
            
            <div className="text-sm text-muted-foreground p-3 bg-muted/30 rounded">
              {customerForm.notes && <div>
                  <strong>Notizen:</strong>
                  <p className="whitespace-pre-wrap text-xs mt-1">{customerForm.notes}</p>
                </div>}
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancelCustomerDialog} disabled={isCustomerCreating} className="flex-1">
                {isCustomerCreating ? 'Wird erstellt...' : 'Abbrechen'}
              </Button>
              <Button onClick={handleCreateCustomer} disabled={!customerForm.firstName || !customerForm.lastName || !customerForm.birthDate || !customerForm.email || isCustomerCreating} className="flex-1">
                {isCustomerCreating ? 'Erstelle...' : 'Erstellen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Verification Dialog */}
      <CustomerVerificationDialog open={showVerificationDialog} customerEmail={verificationEmail} verificationToken={verificationToken} onVerified={handleVerificationComplete} onCancel={() => {
      setShowVerificationDialog(false);
      setVerificationToken('');
      setVerificationEmail('');
      resetCustomerForm();
      setSelectedUnit(null);
    }} />

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Notiz hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedUnit && <div className="text-sm text-muted-foreground">
                {selectedUnit.unit_name ? <p>Für: {selectedUnit.unit_name}</p> : <p>Für: Wohneinheit</p>}
              </div>}
            
            <div>
              <Label htmlFor="note-text">Notiz</Label>
              <Textarea id="note-text" value={note} onChange={e => setNote(e.target.value)} placeholder="Neue Notiz eingeben..." rows={4} />
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
              setShowNoteDialog(false);
              setNote('');
              // Action Dialog bleibt offen, falls Benutzer es abbricht
            }} className="flex-1">
                Abbrechen
              </Button>
              <Button onClick={handleAddNote} disabled={!note.trim() || addingNote} className="flex-1">
                {addingNote ? 'Speichere...' : 'Speichern'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Wohneinheit löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Wohneinheit wirklich löschen?
              {unitToDelete?.unit_name && <span className="font-medium"> "{unitToDelete.unit_name}"</span>}
              <br />
              Alle zugehörigen Notizen und Follow-Ups werden ebenfalls gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUnit} className="bg-red-500 hover:bg-red-600">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* House Notes Dialog */}
      <Dialog open={showHouseNotesDialog} onOpenChange={(open) => {
        setShowHouseNotesDialog(open);
        if (!open) {
          setHouseNoteText('');
          setEditingNoteId(null);
        }
      }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Hausnummer {house?.house_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Existing Notes */}
            {house?.notes && house.notes.length > 0 && (
              <div className="space-y-2">
                <Label>Vorhandene Notizen</Label>
                <div className="space-y-2">
                  {house.notes.map((note) => (
                    <div key={note.id} className="bg-muted/20 rounded-md p-3">
                      {editingNoteId === note.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={houseNoteText}
                            onChange={(e) => setHouseNoteText(e.target.value)}
                            placeholder="Notiz bearbeiten..."
                            className="min-h-[60px]"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={async () => {
                                if (onUpdateHouseNote && houseNoteText.trim() && house) {
                                  await onUpdateHouseNote(note.id, houseNoteText);
                                  setEditingNoteId(null);
                                  setHouseNoteText('');
                                  
                                  // Close the dialog
                                  setShowHouseNotesDialog(false);
                                }
                              }}
                            >
                              Speichern
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingNoteId(null);
                                setHouseNoteText('');
                              }}
                            >
                              Abbrechen
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm mb-2">{note.note}</p>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">
                              {new Date(note.created_at).toLocaleDateString('de-DE', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric',
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </p>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  setEditingNoteId(note.id);
                                  setHouseNoteText(note.note);
                                }}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                onClick={async () => {
                                  if (onDeleteHouseNote && house) {
                                    await onDeleteHouseNote(house.id, note.id);
                                    
                                    // Close the dialog
                                    setShowHouseNotesDialog(false);
                                    setHouseNoteText('');
                                    setEditingNoteId(null);
                                  }
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Add New Note */}
            {editingNoteId === null && (
              <div className="space-y-2">
                <Label>Neue Notiz hinzufügen</Label>
                <Textarea
                  value={houseNoteText}
                  onChange={(e) => setHouseNoteText(e.target.value)}
                  className="min-h-[80px]"
                />
                <Button
                  onClick={async () => {
                    if (onAddHouseNote && house && houseNoteText.trim()) {
                      await onAddHouseNote(house.id, houseNoteText);
                      setHouseNoteText('');
                      
                      // Close the dialog
                      setShowHouseNotesDialog(false);
                    }
                  }}
                  disabled={!houseNoteText.trim()}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Notiz hinzufügen
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* OCR Scanner Modal */}
      <OCRScanner isOpen={showOCRScanner} onClose={() => setShowOCRScanner(false)} onTextScanned={text => {
      if (text && typeof text === 'string' && text.trim()) {
        setUnitName(text.trim());
      }
      setShowOCRScanner(false);
    }} />
      
      {/* Bulk Unit Creator */}
      <BulkUnitCreator 
        open={showBulkCreator} 
        onClose={() => setShowBulkCreator(false)} 
        onCreateUnits={handleBulkCreate} 
        houseId="direct"
        onCreateUnit={async (houseId, unit, options) => {
          await onCreateUnit({
            unit_name: unit.name.trim(),
            tags: [],
            note: unit.notes || undefined,
            reminder: undefined
          }, options);
        }}
        onCreateUnitsBatch={onCreateUnitsBatch ? async (houseId, units, onProgress) => {
          return await onCreateUnitsBatch(units, onProgress);
        } : undefined}
        onNavigateToHouse={() => setShowBulkCreator(false)}
      />
      
      {/* Scroll to top button */}
      {showScrollToTop && <Button variant="default" size="icon" className="fixed bottom-6 right-6 z-50 rounded-full shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 animate-scale-in" onClick={scrollToTop} title="Nach oben scrollen">
          <ChevronUp className="h-5 w-5" />
        </Button>}
    </div>;
};